import { OpenAI } from "openai";
import type { ChatCompletionMessageParam } from "openai/resources/index.js";
import { z } from "zod";
import type { Message } from "@/shared/messages";
import { zodObjectWithTelemetry } from "@/telemetry/helpers/zod";
import { LLMBase, type LLMGenerateMessageJob, type LLMGenerateObjectChunk } from "../base";
import type { LLMToolDefinition } from "../resources";

// Config
export const openAILLMConfig = zodObjectWithTelemetry({
  schema: z.object({
    provider: z.literal("openai"),
    apiKey: z.string().prefault(process.env.OPENAI_API_KEY as string),
    model: z.enum(["gpt-4o-mini", "gpt-4o", "gpt-5", "gpt-5-nano"]).prefault("gpt-4o"),
    temperature: z.number().min(0).max(2).prefault(1),
  }),
  toTelemetry: (config) => {
    // Redact sensitive fields
    config.apiKey = "redacted" as never;
    return config;
  },
});

// Model
export class OpenAILLM extends LLMBase<typeof openAILLMConfig.schema> {
  readonly #client: OpenAI;

  constructor(config: z.input<typeof openAILLMConfig.schema>) {
    super(openAILLMConfig.schema, config);
    this.#client = new OpenAI({ apiKey: config.apiKey });
  }

  /**
   * Format conversion
   */

  #toOpenAIMessage(message: Message): ChatCompletionMessageParam {
    if (message.role === "user") {
      return { role: "user", content: message.content };
    }

    if (message.role === "agent") {
      return {
        role: "assistant",
        content: message.content,
        ...(message.toolsRequests?.length
          ? {
              tool_calls: message.toolsRequests?.map((request) => ({
                id: request.toolRequestId,
                function: { name: request.toolName, arguments: JSON.stringify(request.toolInput) },
                type: "function",
              })),
            }
          : {}),
      };
    }

    if (message.role === "system") {
      return { role: "system", content: message.content };
    }

    if (message.role === "tool") {
      return {
        role: "tool",
        tool_call_id: message.toolName,
        content: JSON.stringify(message.toolOutput),
      };
    }

    return null as never;
  }

  #toOpenAIMessages(messages: Message[]): ChatCompletionMessageParam[] {
    return messages.map(this.#toOpenAIMessage);
  }

  #toOpenAITool(tool: LLMToolDefinition): OpenAI.Chat.Completions.ChatCompletionTool {
    return {
      type: "function",
      function: {
        name: tool.name,
        description: tool.description,
        parameters: z.toJSONSchema(tool.schema.input),
      },
    };
  }

  #toOpenAITools(tools: LLMToolDefinition[]): OpenAI.Chat.Completions.ChatCompletionTool[] {
    return tools.map(this.#toOpenAITool);
  }

  /**
   * Generate a message with job management - returns jobId along with stream
   */
  async generateMessage(
    params: Parameters<typeof LLMBase.prototype.generateMessage>[0],
  ): Promise<LLMGenerateMessageJob> {
    // Create a new job
    const job = this.createGenerateMessageJob();

    // Prepare tools and messages in OpenAI format
    const openaiTools = params.tools.length > 0 ? this.#toOpenAITools(params.tools) : undefined;
    const openaiMessages = this.#toOpenAIMessages(params.messages);

    // Prepare job stream
    const stream = await this.#client.chat.completions.create(
      {
        model: this.config.model,
        temperature: this.config.temperature,
        messages: openaiMessages,
        stream: true,
        ...(openaiTools?.length
          ? {
              tools: openaiTools,
              parallel_tool_calls: true,
            }
          : {}),
      },
      { signal: job._abortController.signal }, // Allows the stream to be cancelled
    );

    // Start streaming in the background (don't await)
    (async () => {
      let pendingToolCalls: Record<string, { id: string; name: string; arguments: string }> = {};

      for await (const chunk of stream) {
        // Ignore chunks if job was cancelled
        if (job._abortController.signal.aborted) break;

        // Extract the choice and delta (if any)
        const choice = chunk.choices[0];
        if (!choice) throw new Error("No choice");
        const delta = choice.delta;

        // Handle content tokens
        if (delta.content) job.stream.push({ type: "content", content: delta.content });

        // Handle tool calls tokens
        if (delta.tool_calls) {
          for (const toolCall of delta.tool_calls) {
            // Retrieve the tool call ID
            const id = toolCall.id ?? Object.keys(pendingToolCalls).at(-1);
            if (!id) throw new Error("No tool call ID");

            // Ensure the tool call is tracked
            if (!pendingToolCalls[id]) pendingToolCalls[id] = { id, name: "", arguments: "" };

            // Compound name tokens
            if (toolCall.function?.name) pendingToolCalls[id].name += toolCall.function.name;

            // Compound arguments tokens
            if (toolCall.function?.arguments)
              pendingToolCalls[id].arguments += toolCall.function.arguments;
          }
        }

        // Handle finish reasons
        // - Tool calls completion
        if (choice.finish_reason === "tool_calls") {
          job.stream.push({
            type: "tools",
            tools: Object.values(pendingToolCalls).map((toolCall) => ({
              toolRequestId: toolCall.id,
              toolName: toolCall.name,
              toolInput: JSON.parse(toolCall.arguments || "{}"),
            })),
          });
          pendingToolCalls = {};
        }

        // - End of stream
        if (choice.finish_reason === "stop") job.stream.push({ type: "end" });
      }
    })();

    // Return the job immediately
    return job;
  }

  async generateObject<T extends z.ZodObject>(params: {
    messages: Message[];
    schema: T;
  }): Promise<LLMGenerateObjectChunk<T>> {
    try {
      // Prepare messages in OpenAI format
      const openaiMessages = this.#toOpenAIMessages(params.messages);

      // Prepare JSON schema
      const jsonSchema = z.toJSONSchema(params.schema);

      // Generate the object
      const response = await this.#client.chat.completions.create({
        model: this.config.model,
        messages: openaiMessages,
        temperature: this.config.temperature,
        response_format: {
          type: "json_schema",
          json_schema: { name: "avc", schema: jsonSchema },
        },
      });

      // Parse the response
      const data = JSON.parse(response.choices[0]?.message?.content || "{}");

      // Return the object
      return { type: "content", data };
    } catch (error) {
      return {
        type: "error",
        error: error instanceof Error ? error.message : "Failed to generate object",
      };
    }
  }
}
